import axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios';
import { jwtDecode } from 'jwt-decode';

import { showMessage } from '../showMessage';

interface TokenResponse {
  token: string;
  success?: boolean;
}

interface JwtPayload {
  userId: number;
  exp?: number;
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
const REFRESH_URL = `${API_BASE_URL}/api/auth/refresh`;

let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];
let hasRedirectedToAuth = false;

const onRefreshed = (token: string) => {
  for (const callback of refreshSubscribers) {
    callback(token);
  }
  refreshSubscribers = [];
};

const addRefreshSubscriber = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

const redirectToAuth = () => {
  if (hasRedirectedToAuth) {
    return;
  }
  hasRedirectedToAuth = true;

  showMessage('error', 'Session expired', 'Your session has expired. Please log in again.');

  window.location.href = '/authUser';
};

const refreshToken = async (): Promise<void> => {
  const token = localStorage.getItem('token');
  if (!token) {
    return;
  }

  try {
    const decoded = jwtDecode<JwtPayload>(token);
    const now = Date.now() / 1000;
    if (decoded.exp && decoded.exp - now < 60 * 60) {
      const newToken = await getNewToken();
      if (newToken) {
        setAuthToken(newToken);
      } else {
        setAuthToken(null);
        redirectToAuth();
      }
    }
  } catch (error) {
    console.error('Error decoding token:', error);
    setAuthToken(null);
    redirectToAuth();
  }
};

interface AuthAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
  skipAuthRefresh?: boolean;
}

export const setAuthToken = (token: string | null) => {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('token', token);
  } else {
    delete axios.defaults.headers.common['Authorization'];
    localStorage.removeItem('token');
  }
};

export const getNewToken = async (): Promise<string | null> => {
  try {
    const currentToken = localStorage.getItem('token');
    if (!currentToken) {
      return null;
    }

    const cfg: AuthAxiosRequestConfig = {
      headers: { Authorization: `Bearer ${currentToken}` },
      skipAuthRefresh: true,
    };

    const response: AxiosResponse<TokenResponse> = await axios.post<
      TokenResponse,
      AxiosResponse<TokenResponse>
    >(REFRESH_URL, {}, cfg);

    if (!response.data?.token || response.data.success === false) {
      setAuthToken(null);
      return null;
    }

    const { token } = response.data;
    setAuthToken(token);
    return token;
  } catch (error) {
    console.error('Error refreshing token:', error);
    setAuthToken(null);
    return null;
  }
};

export const setupTokenRefresh = () => {
  void refreshToken();
  const intervalId = setInterval(
    () => {
      void refreshToken();
    },
    1000 * 60 * 15,
  );
  return () => clearInterval(intervalId);
};

export const setupAxiosInterceptors = () => {
  axios.interceptors.response.use(
    (response) => response,
    async (error__: unknown) => {
      if (!axios.isAxiosError(error__)) {
        throw error__;
      }

      const error = error__;
      const originalRequest = (error.config || {}) as AuthAxiosRequestConfig;
      const status: number | undefined = error.response?.status;
      const url = String(originalRequest.url || '');
      const isAuthFlowRequest =
        url.includes('/api/auth/login') ||
        url.includes('/api/auth/register') ||
        url.includes('/api/auth/verify-phone') ||
        url.includes('/api/auth/verify-email') ||
        url.includes('/api/auth/resend-verification-code');

      if (status !== 401) {
        throw error__;
      }

      try {
        if (originalRequest.skipAuthRefresh || url.includes('/api/auth/refresh') || isAuthFlowRequest) {
          setAuthToken(null);
          throw error__;
        }

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            addRefreshSubscriber((newToken: string) => {
              if (!newToken) {
                reject(error);
                return;
              }
              try {
                const headers = (originalRequest.headers ?? {}) as Record<string, string>;
                headers.Authorization = `Bearer ${newToken}`;
                originalRequest.headers = headers;
                resolve(axios(originalRequest));
              } catch (error_) {
                reject(error_);
              }
            });
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const newToken = await getNewToken();
        isRefreshing = false;

        if (!newToken) {
          onRefreshed('');
          setAuthToken(null);
          redirectToAuth();
          throw error__;
        }

        onRefreshed(newToken);

        const headers = (originalRequest.headers ?? {}) as Record<string, string>;
        headers.Authorization = `Bearer ${newToken}`;
        originalRequest.headers = headers;
        return axios(originalRequest);
      } catch (error_) {
        const expectedAuthFlowFailure =
          isAuthFlowRequest && axios.isAxiosError(error_) && error_.response?.status === 401;
        if (!expectedAuthFlowFailure) {
          console.error('Interceptor error:', error_);
          redirectToAuth();
        }
        setAuthToken(null);
        throw error__;
      }
    },
  );
};

export const logout = () => {
  setAuthToken(null);
  redirectToAuth();
};
